var _dec, _class, _descriptor, _descriptor2, _descriptor3;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import JSONSerializer from '@ember-data/serializer/json';
import { underscore, dasherize } from '@ember/string';
import { inject as service } from '@ember/service';
import { CatalogObjectType } from '@square/dashboard-items-data/models/connect/catalog-object';
import { isNone } from '@ember/utils';
const SERIALIZE_KEYS = new Set(['id', 'ids']);

/**
 * This serializer supports additional configurations on the `attrs` object:
 *   `serializedOnFragment` - pass this option for an embedded relationship to indicate that the embedded
 *   related objects should be serialized to and normalized from a child fragment. This is handy because
 *   many of our APIs include related objects on nested JSON data, but ember model fragments don't currently
 *   support EmberData relationships to other models.
 *
 *   `serializedOnKey` - pass this option in conjunction with `serializedOnFragment` for non-embeded relationships
 *   to indicate the key to normalize and serilaize to on the child fragment.
 *
 *   When using this serializer option you should be able write the rest of your code as if the embedded models
 *   are directly on the parent object
 *
 *   Example usage:
 *   export default CatalogObjectSerializer.extend(EmbeddedRecordsMixin, {
 *     attrs: {
 *       values: { embedded: 'always', serializedOnFragment: 'item_option_data' },
 *       images: {
 *         serialize: 'ids',
           deserialize: 'ids',
           serializedOnFragment: 'category_data',
           serializedOnKey: 'image_ids',
         },
 *     },
 *   });
});
 */
let CatalogObject = (_dec = service('buyer-subs/itemization-status'), _class = class CatalogObject extends JSONSerializer {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "catalog", _descriptor, this);
    _initializerDefineProperty(this, "features", _descriptor2, this);
    _initializerDefineProperty(this, "itemizationStatus", _descriptor3, this);
  }
  keyForAttribute(attr) {
    return underscore(attr);
  }
  keyForRelationship(key, relationship) {
    if (key === 'image') {
      return 'image_id';
    }
    return super.keyForRelationship(key, relationship);
  }
  normalizeForPush(type, object) {
    for (const [key, value] of type.attributes.entries()) {
      const normalizedKey = this.keyForAttribute(key);
      if (!Object.keys(object).includes(normalizedKey)) {
        if (value.kind === 'array') {
          object[normalizedKey] = [];
        } else {
          object[normalizedKey] = null;
        }
      }
    }
    return this.normalize(type, object);
  }
  normalize(type, object) {
    // Normalize custom attribute values to a fragment array, since EmberData doesn't handle maps well
    object.custom_attribute_values = Object.values(object.custom_attribute_values ?? {});
    for (const [key, config] of Object.entries(this.attrs || {})) {
      // check if there are non-embeded relationships serialized on a
      // fragment
      const hasSerializedAttributes = SERIALIZE_KEYS.has(config?.serialize) && SERIALIZE_KEYS.has(config?.deserialize);
      if ((config?.embedded === 'always' || hasSerializedAttributes) && config.serializedOnFragment && object[config.serializedOnFragment]) {
        // if the key is different for non-embeded relationships, the
        // correct key should be specified
        const underscoredKey = underscore(key);
        const keyToSerialize = config.serializedOnKey ?? underscoredKey;
        const value = object[config.serializedOnFragment][keyToSerialize];
        if (!isNone(value)) {
          // Use camelcase `attr` key, unless config specifies underscore
          const keyToWrite = config?.useUnderscoreKey ? underscoredKey : key;
          object[keyToWrite] = value;
          delete object[config.serializedOnFragment][keyToSerialize];
        }
      }
    }
    const normalized = super.normalize(type, object);
    if (object.related_objects) {
      const relatedObjects = this._normalizeRelatedObjects(object.related_objects);
      normalized.included = [...(normalized.included || []), ...relatedObjects];
    }
    return normalized;
  }
  serialize(snapshot, options = {}) {
    if (options.rpc) {
      this.catalog.setSerializeForRpc(true);
    }
    const json = super.serialize(snapshot, {
      includeId: true
    });
    if (!json.id && snapshot.record.isNew) {
      json.id = snapshot.record.idOrTempId();
    }
    for (const [key, config] of Object.entries(this.attrs || {})) {
      // Check if there are non-embeded relationships serialized on a
      // fragment
      const hasSerializedAttributes = SERIALIZE_KEYS.has(config?.serialize) && SERIALIZE_KEYS.has(config?.deserialize);
      if ((config?.embedded === 'always' || hasSerializedAttributes) && config.serializedOnFragment && json[config.serializedOnFragment]) {
        // if the key is different for non-embeded relationships, the
        // correct key should be specified
        const underscoredKey = underscore(key);
        const keyToSerialize = config.serializedOnKey ?? underscoredKey;
        const keyToMove = json[underscoredKey] ? underscoredKey : key;
        json[config.serializedOnFragment][keyToSerialize] = json[keyToMove];
        delete json[keyToMove];
      }
    }

    // Convert to a map to match proto definition
    json.custom_attribute_values = (json.custom_attribute_values ?? []).reduce((accum, customAttributeValue) => {
      accum[customAttributeValue.key] = customAttributeValue;
      return accum;
    }, {});
    if (options.rpc) {
      this.catalog.setSerializeForRpc(false);
    }
    return json;
  }

  // Sets the correct id on nested hasMany relationships. For example,
  // Category hasMany AvailabilityPeriods, we need to set
  // category["availabilityPeriods"] = ["AVAILABILITY_PERIOD-1, AVAILABILITY_PERIOD-2"]
  // before sending it over
  serializeHasMany(snapshot, json, relationship) {
    const key = relationship.key;
    const hasMany = snapshot.hasMany(key);
    if (Array.isArray(hasMany) && this.attrs[key]) {
      const relationalIds = hasMany.map(entity => {
        return entity.record.idOrTempId();
      });
      json[key] = relationalIds;
    } else {
      super.serializeHasMany(snapshot, json, relationship);
    }
  }

  // Sets the correct id or temp id on nested belongsTo relationships.
  serializeBelongsTo(snapshot, json, relationship) {
    const key = relationship.key;
    const belongsTo = snapshot.belongsTo(key);
    if (belongsTo && this.attrs[key]) {
      const relationalId = belongsTo.record?.idOrTempId();
      json[key] = relationalId;
    } else {
      super.serializeBelongsTo(snapshot, json, relationship);
    }
  }
  pushPayload(store, payload) {
    const modelClass = store.modelFor(this._modelNameForObjectType(payload.type));
    const normalized = this.normalizeSingleResponse(store, modelClass, payload);
    store.push(normalized);
  }
  _modelNameForObjectType(type) {
    switch (type) {
      case CatalogObjectType.ITEM_OPTION_VAL:
        return 'connect/item-option-value';
      case CatalogObjectType.SALE_PRICE:
        return 'connect/item-variation/sale-price';
      case CatalogObjectType.SUBSCRIPTION_PLAN_VARIATION:
        if (this.itemizationStatus.isItemized || this.features.isEnabled('buyersubs/updated_legacy_api_version')) {
          return 'connect/subscription-plan/plan-variation';
        } else {
          return 'connect/subscription-plan';
        }
      default:
        return `connect/${dasherize(type.toLowerCase())}`;
    }
  }
  _normalizeRelatedObjects(objects) {
    return objects
    // NOTE (alexbbt): This filters out models that are not yet supported.
    // Mostly this is just the types with a connect ember data model.
    .filter(object => ['SUBSCRIPTION_PLAN', 'SUBSCRIPTION_PLAN_VARIATION', 'ITEM', 'IMAGE', 'ADDRESS', 'AVAILABILITY_PERIOD', 'TAX', 'DISCOUNT'].includes(object.type)).reduce((included, object) => {
      // NOTE (amcconnell): this fixes a bug with Catalog changers that causes
      // a downgraded Plan Variation to still have type SUBSCRIPTION_PLAN_VARIATION
      // even after it has been converted to a SUBSCRIPTION_PLAN. This is only causing
      // issues in related_objects. Once SOC Checkout Links have updated to use Plan Variations,
      // it can be removed
      if (object.type === CatalogObjectType.SUBSCRIPTION_PLAN_VARIATION) {
        object.type = CatalogObjectType.SUBSCRIPTION_PLAN;
      }
      const modelName = this._modelNameForObjectType(object.type);
      const modelClass = this.store.modelFor(modelName);
      const serializer = this.store.serializerFor(modelClass.modelName);
      const serialized = serializer.normalize(modelClass, object);
      return [...included, serialized.data, ...(serialized.included || [])];
    }, []);
  }
  serializeAttribute(snapshot, json, key, attribute) {
    // Only serialize enums to their proto equivalent when crafting RPC requests.
    // Consumers can set `serializeForRpc` on the catalog service when proto enum serialization is desired.
    if (this.catalog.serializeForRpc && attribute.options?.protoEnum) {
      const protoEnum = attribute.options.protoEnum;
      const value = snapshot.attr(key);
      const serializedValue = Array.isArray(value) ? value.map(obj => protoEnum[obj]) : protoEnum[value];
      json[this.keyForAttribute(key)] = serializedValue;
    } else {
      super.serializeAttribute(snapshot, json, key, attribute);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "catalog", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "features", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "itemizationStatus", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
export { CatalogObject as default };